import { useState, useEffect, useCallback, type FormEvent } from 'react';
import {
    fetchClasses, createClass, updateClass,
    createSection, updateSection,
    mapClassToUi,
    type ClassUi, type SectionApiRecord,
} from '../../services/classApi';
import type { ApiError } from '../../services/api';
import { fetchStaffList, type StaffApiRecord } from '../../services/staffApi';
import './ClassManagement.css';
import { useTranslation } from 'react-i18next';
import { getSchoolId } from '../../services/auth';

/* ── Helpers ── */
function formatTimestamp(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function getInitials(name: string): string {
    return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
}

/* ── Types ── */
type DrawerMode = null | 'create-class' | 'edit-class' | 'create-section' | 'edit-section';

interface ClassFormErrors { name?: string; }
interface SectionFormErrors { name?: string; capacity?: string; class_teacher_id?: string; }

/* ── Component ── */
export default function ClassManagement() {
    const { t: _t } = useTranslation();
    /* ---- data ---- */
    const [classes, setClasses] = useState<ClassUi[]>([]);
    const [loading, setLoading] = useState(true);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    /* ---- expanded rows ---- */
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

    /* ---- drawer ---- */
    const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [parentClassId, setParentClassId] = useState<string | null>(null);

    /* ---- class form ---- */
    const [className, setClassName] = useState('');
    const [classStandard, setClassStandard] = useState('');
    const [classCapacity, setClassCapacity] = useState('');
    const [classHasSections, setClassHasSections] = useState(true);
    const [classTeacherId, setClassTeacherId] = useState<string | null>(null);
    const [classIsActive, setClassIsActive] = useState(true);
    const [classErrors, setClassErrors] = useState<ClassFormErrors>({});

    /* ---- section form ---- */
    const [sectionName, setSectionName] = useState('');
    const [sectionCapacity, setSectionCapacity] = useState('');
    const [sectionTeacherId, setSectionTeacherId] = useState<string | null>(null);
    const [sectionIsActive, setSectionIsActive] = useState(true);
    const [sectionErrors, setSectionErrors] = useState<SectionFormErrors>({});

    /* ---- staff list for teacher select ---- */
    const [staffList, setStaffList] = useState<StaffApiRecord[]>([]);

    /* ---- submitting ---- */
    const [isSubmitting, setIsSubmitting] = useState(false);

    /* ---- delete confirm ---- */
    const [deleteTarget, setDeleteTarget] = useState<{ type: 'class' | 'section'; id: string; name: string } | null>(null);

    /* ---- timestamps ---- */
    const [editCreatedAt, setEditCreatedAt] = useState('');
    const [editUpdatedAt, setEditUpdatedAt] = useState('');

    /* ============== Load data ============== */
    const loadClasses = useCallback(async () => {
        setLoading(true);
        setErrorMsg('');
        try {
            const [res, staffRes] = await Promise.all([
                fetchClasses({ perPage: 100 }),
                fetchStaffList({ perPage: 100 }),
            ]);
            setClasses(res.data.map(mapClassToUi));
            setStaffList(staffRes.data);
        } catch (err) {
            const apiErr = err as ApiError;
            setErrorMsg(apiErr.message || 'Failed to load classes.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadClasses();
    }, [loadClasses]);

    /* ============== Expand toggle ============== */
    const toggleExpand = (id: string) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    /* ============== Get sections for a class ============== */
    const getSections = (cls: ClassUi) => cls.sections ?? [];

    /* ============== Reset forms ============== */
    const resetClassForm = useCallback(() => {
        setClassName('');
        setClassStandard('');
        setClassCapacity('');
        setClassHasSections(true);
        setClassTeacherId(null);
        setClassIsActive(true);
        setClassErrors({});
        setEditingId(null);
        setEditCreatedAt('');
        setEditUpdatedAt('');
    }, []);

    const resetSectionForm = useCallback(() => {
        setSectionName('');
        setSectionCapacity('');
        setSectionTeacherId(null);
        setSectionIsActive(true);
        setSectionErrors({});
        setEditingId(null);
        setParentClassId(null);
        setEditCreatedAt('');
        setEditUpdatedAt('');
    }, []);

    /* ============== Drawer openers ============== */
    const openCreateClass = () => {
        resetClassForm();
        resetSectionForm();
        setDrawerMode('create-class');
    };

    const openEditClass = (c: ClassUi) => {
        resetClassForm();
        resetSectionForm();
        setClassName(c.name);
        setClassStandard(String(c.standard));
        setClassCapacity(c.capacity != null ? String(c.capacity) : '');
        setClassHasSections(c.has_sections);
        setClassTeacherId(c.class_teacher_id);
        setClassIsActive(c.is_active);
        setEditingId(c.id);
        setEditCreatedAt(c.created_at);
        setEditUpdatedAt(c.updated_at);
        setDrawerMode('edit-class');
    };

    const openCreateSection = (classId: string) => {
        resetClassForm();
        resetSectionForm();
        setParentClassId(classId);
        setDrawerMode('create-section');
    };

    const openEditSection = (sec: SectionApiRecord) => {
        resetClassForm();
        resetSectionForm();
        setSectionName(sec.name);
        setSectionCapacity(String(sec.capacity));
        setSectionTeacherId(sec.class_teacher_id);
        setSectionIsActive(sec.is_active);
        setEditingId(sec.id);
        setParentClassId(sec.class_id);
        setEditCreatedAt(sec.created_at);
        setEditUpdatedAt(sec.updated_at);
        setDrawerMode('edit-section');
    };

    const closeDrawer = () => {
        setDrawerMode(null);
        resetClassForm();
        resetSectionForm();
    };

    /* ============== Validation ============== */
    const validateClass = (): boolean => {
        const e: ClassFormErrors = {};
        if (!className.trim()) e.name = 'Class name is required.';
        setClassErrors(e);
        return Object.keys(e).length === 0;
    };

    const validateSection = (): boolean => {
        const e: SectionFormErrors = {};
        if (!sectionName.trim()) e.name = 'Section name is required.';
        else if (sectionName.trim().length > 10) e.name = 'Section name must be at most 10 characters.';
        if (!sectionCapacity) e.capacity = 'Capacity is required.';
        else if (isNaN(Number(sectionCapacity)) || !Number.isInteger(Number(sectionCapacity)) || Number(sectionCapacity) < 1)
            e.capacity = 'Capacity must be a positive integer.';
        setSectionErrors(e);
        return Object.keys(e).length === 0;
    };

    /* ============== Submit ============== */
    const handleSubmit = async (ev: FormEvent) => {
        ev.preventDefault();
        const isClassForm = drawerMode === 'create-class' || drawerMode === 'edit-class';
        if (isClassForm && !validateClass()) return;
        if (!isClassForm && !validateSection()) return;
        setIsSubmitting(true);
        setErrorMsg('');

        try {
            if (isClassForm) {
                const payload = {
                    name: className.trim(),
                    standard: classStandard ? Number(classStandard) : classes.length + 1,
                    capacity: classCapacity ? Number(classCapacity) : null,
                    has_sections: classHasSections,
                    class_teacher_id: classTeacherId,
                    is_active: classIsActive,
                    metadata: null,
                    school_id:getSchoolId()
                };

                if (drawerMode === 'edit-class' && editingId) {
                    await updateClass(editingId, payload);
                    setSuccessMsg('Class updated successfully!');
                } else {
                    await createClass(payload);
                    setSuccessMsg('Class created successfully!');
                }
            } else {
                const payload = {
                    name: sectionName.trim(),
                    capacity: Number(sectionCapacity),
                    class_teacher_id: sectionTeacherId,
                    is_active: sectionIsActive,
                };

                if (drawerMode === 'edit-section' && editingId && parentClassId) {
                    await updateSection(parentClassId, editingId, payload);
                    setSuccessMsg('Section updated successfully!');
                } else if (parentClassId) {
                    await createSection(parentClassId, payload);
                    setExpandedIds((prev) => new Set(prev).add(parentClassId));
                    setSuccessMsg('Section created successfully!');
                }
            }

            closeDrawer();
            await loadClasses();
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err) {
            const apiErr = err as ApiError;
            setErrorMsg(apiErr.message || 'Failed to save. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ============== Delete ============== */
    const confirmDelete = () => {
        if (!deleteTarget) return;
        if (deleteTarget.type === 'class') {
            setClasses((prev) => prev.filter((c) => c.id !== deleteTarget.id));
        } else {
            setClasses((prev) =>
                prev.map((c) => ({
                    ...c,
                    sections: c.sections.filter((s) => s.id !== deleteTarget.id),
                }))
            );
        }
        setDeleteTarget(null);
        setSuccessMsg(`${deleteTarget.type === 'class' ? 'Class' : 'Section'} removed successfully!`);
        setTimeout(() => setSuccessMsg(''), 5000);
    };

    /* ============== Staff name helpers ============== */
    const getStaffName = (s: StaffApiRecord) =>
        [s.user?.first_name, s.user?.last_name].filter(Boolean).join(' ');

    const getTeacherName = (teacherId: string | null) => {
        if (!teacherId) return null;
        const staff = staffList.find((s) => s.id === teacherId);
        return staff ? getStaffName(staff) : null;
    };

    /* ============== Metric helpers ============== */
    const metricColor = (strength: number, capacity: number) => {
        if (capacity === 0) return '';
        const ratio = strength / capacity;
        if (ratio >= 1) return 'danger';
        if (ratio >= 0.9) return 'warning';
        return '';
    };

    /* ================================================================= */
    /*  R E N D E R                                                       */
    /* ================================================================= */

    const isClassDrawer = drawerMode === 'create-class' || drawerMode === 'edit-class';
    const isSectionDrawer = drawerMode === 'create-section' || drawerMode === 'edit-section';

    return (
        <div className="classes">
            {/* Success toast */}
            {successMsg && (
                <div className="classes__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <span>{successMsg}</span>
                    <button className="classes__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {/* Error toast */}
            {errorMsg && (
                <div className="classes__toast classes__toast--error">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    <span>{errorMsg}</span>
                    <button className="classes__toast-dismiss" onClick={() => setErrorMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {/* Header */}
            <div className="classes__header">
                <div className="classes__header-text">
                    <h1 className="classes__title">Classes & Sections</h1>
                    <p className="classes__subtitle">
                        Manage your academic class hierarchy and section capacity.
                    </p>
                </div>
                <div className="classes__header-actions">
                    <button className="classes__add-btn" onClick={openCreateClass}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Add Class
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="classes__table-wrap">
                {loading ? (
                    <div className="classes__loading">
                        <div className="classes__loader">
                            <span className="classes__loader-dot" />
                            <span className="classes__loader-dot" />
                            <span className="classes__loader-dot" />
                        </div>
                    </div>
                ) : classes.length === 0 ? (
                    <div className="classes__empty">
                        <div className="classes__empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                            </svg>
                        </div>
                        <h3 className="classes__empty-title">No classes yet</h3>
                        <p className="classes__empty-text">Add your first class to get started.</p>
                    </div>
                ) : (
                    <table className="classes__table">
                        <thead>
                            <tr>
                                <th className="classes__th" style={{ width: 32 }}></th>
                                <th className="classes__th">Class Name</th>
                                <th className="classes__th">Class Teacher</th>
                                <th className="classes__th classes__th--center">Sections</th>
                                <th className="classes__th classes__th--center">Strength / Capacity</th>
                                <th className="classes__th classes__th--center">Status</th>
                                <th className="classes__th classes__th--actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {classes.map((c) => {
                                const isExpanded = expandedIds.has(c.id);
                                const classSections = getSections(c);
                                const teacherName = getTeacherName(c.class_teacher_id);
                                const totalStrength = classSections.reduce((sum, s) => sum + (s.students_count ?? s.strength), 0);
                                const totalCapacity = classSections.reduce((sum, s) => sum + s.capacity, 0);
                                const totalColor = metricColor(totalStrength, totalCapacity);
                                const totalPct = totalCapacity > 0
                                    ? Math.min((totalStrength / totalCapacity) * 100, 100)
                                    : 0;

                                return (
                                    <>
                                        {/* Class row */}
                                        <tr
                                            key={c.id}
                                            className="classes__class-row"
                                            onClick={() => toggleExpand(c.id)}
                                        >
                                            <td className="classes__td">
                                                <span className={`classes__expand-icon ${isExpanded ? 'classes__expand-icon--open' : ''}`}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="9 18 15 12 9 6" />
                                                    </svg>
                                                </span>
                                            </td>
                                            <td className="classes__td classes__td--name">{c.name}</td>
                                            <td className="classes__td">
                                                {teacherName ? (
                                                    <span className="classes__teacher">
                                                        <span className="classes__teacher-avatar">
                                                            {getInitials(teacherName)}
                                                        </span>
                                                        <span className="classes__teacher-name">{teacherName}</span>
                                                    </span>
                                                ) : (
                                                    <span className="classes__muted">Unassigned</span>
                                                )}
                                            </td>
                                            <td className="classes__td classes__td--center">
                                                <span className="classes__section-count">{classSections.length}</span>
                                            </td>
                                            <td className="classes__td classes__td--center">
                                                <div className="classes__metric">
                                                    <span className={`classes__metric-text ${totalColor ? `classes__metric-text--${totalColor}` : ''}`}>
                                                        {totalStrength} / {totalCapacity}
                                                    </span>
                                                    <div className="classes__metric-bar">
                                                        <div
                                                            className={`classes__metric-fill ${totalColor ? `classes__metric-fill--${totalColor}` : ''}`}
                                                            style={{ width: `${totalPct}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="classes__td classes__td--center">
                                                <span className={`classes__badge ${c.is_active ? 'classes__badge--active' : 'classes__badge--inactive'}`}>
                                                    {c.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="classes__td classes__td--actions" onClick={(e) => e.stopPropagation()}>
                                                <button className="classes__row-btn" onClick={() => openEditClass(c)}>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                    </svg>
                                                    Edit
                                                </button>
                                                <button
                                                    className="classes__row-btn classes__row-btn--delete"
                                                    style={{ marginLeft: 4 }}
                                                    onClick={() => setDeleteTarget({ type: 'class', id: c.id, name: c.name })}
                                                >
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6" />
                                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>

                                        {/* Sections sub-table (expanded) */}
                                        {isExpanded && (
                                            <tr key={`${c.id}-sections`} className="classes__sections-row">
                                                <td colSpan={7} className="classes__sections-cell">
                                                    <div className="classes__sections-wrap">
                                                        <div className="classes__sections-header">
                                                            <span className="classes__sections-title">
                                                                Sections — {c.name}
                                                            </span>
                                                            <button
                                                                className="classes__add-btn classes__add-btn--sm"
                                                                onClick={() => openCreateSection(c.id)}
                                                            >
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                    <line x1="12" y1="5" x2="12" y2="19" />
                                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                                </svg>
                                                                Add Section
                                                            </button>
                                                        </div>

                                                        {classSections.length === 0 ? (
                                                            <p className="classes__no-sections">
                                                                No sections created yet for this class.
                                                            </p>
                                                        ) : (
                                                            <table className="classes__sections-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Section</th>
                                                                        <th style={{ textAlign: 'center' }}>Capacity</th>
                                                                        <th>Strength / Capacity</th>
                                                                        <th style={{ textAlign: 'center' }}>Status</th>
                                                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {classSections.map((sec) => {
                                                                        const strength = sec.students_count ?? sec.strength;
                                                                        const color = metricColor(strength, sec.capacity);
                                                                        const pct = sec.capacity > 0
                                                                            ? Math.min((strength / sec.capacity) * 100, 100)
                                                                            : 0;

                                                                        return (
                                                                            <tr key={sec.id}>
                                                                                <td style={{ fontWeight: 600 }}>{sec.name}</td>
                                                                                <td style={{ textAlign: 'center' }}>{sec.capacity}</td>
                                                                                <td>
                                                                                    <div className="classes__metric">
                                                                                        <span className={`classes__metric-text ${color ? `classes__metric-text--${color}` : ''}`}>
                                                                                            {strength} / {sec.capacity}
                                                                                        </span>
                                                                                        <div className="classes__metric-bar">
                                                                                            <div
                                                                                                className={`classes__metric-fill ${color ? `classes__metric-fill--${color}` : ''}`}
                                                                                                style={{ width: `${pct}%` }}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td style={{ textAlign: 'center' }}>
                                                                                    <span className={`classes__badge ${sec.is_active ? 'classes__badge--active' : 'classes__badge--inactive'}`}>
                                                                                        {sec.is_active ? 'Active' : 'Inactive'}
                                                                                    </span>
                                                                                </td>
                                                                                <td style={{ textAlign: 'right' }}>
                                                                                    <button className="classes__row-btn" onClick={() => openEditSection(sec)}>
                                                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                                                        </svg>
                                                                                        Edit
                                                                                    </button>
                                                                                    <button
                                                                                        className="classes__row-btn classes__row-btn--delete"
                                                                                        style={{ marginLeft: 4 }}
                                                                                        onClick={() => setDeleteTarget({ type: 'section', id: sec.id, name: `${c.name} - ${sec.name}` })}
                                                                                    >
                                                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                                            <polyline points="3 6 5 6 21 6" />
                                                                                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                                                        </svg>
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ============= Drawer ============= */}
            {drawerMode && (
                <>
                    <div className="classes__overlay" onClick={closeDrawer} />
                    <aside className="classes__drawer">
                        <div className="classes__drawer-header">
                            <h2 className="classes__drawer-title">
                                {drawerMode === 'create-class' && 'Add Class'}
                                {drawerMode === 'edit-class' && 'Edit Class'}
                                {drawerMode === 'create-section' && 'Add Section'}
                                {drawerMode === 'edit-section' && 'Edit Section'}
                            </h2>
                            <button className="classes__drawer-close" onClick={closeDrawer} title="Close">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <form className="classes__drawer-body" onSubmit={handleSubmit} noValidate id="class-form">
                            {/* ---- Class Form ---- */}
                            {isClassDrawer && (
                                <>
                                    <div className="classes__card">
                                        <h3 className="classes__card-title">Class Details</h3>

                                        <div className="classes__form-row">
                                            <div className="classes__field">
                                                <label className="classes__label classes__label--required" htmlFor="className">Class Name</label>
                                                <input
                                                    className={`classes__input ${classErrors.name ? 'classes__input--error' : ''}`}
                                                    type="text"
                                                    id="className"
                                                    placeholder="e.g. Grade 4"
                                                    value={className}
                                                    onChange={(e) => {
                                                        setClassName(e.target.value);
                                                        if (classErrors.name) setClassErrors({});
                                                    }}
                                                    autoFocus
                                                />
                                                {classErrors.name && <p className="classes__error">{classErrors.name}</p>}
                                            </div>

                                            <div className="classes__field">
                                                <label className="classes__label" htmlFor="classStandard">Standard</label>
                                                <input
                                                    className="classes__input"
                                                    type="number"
                                                    id="classStandard"
                                                    placeholder="e.g. 10"
                                                    value={classStandard}
                                                    onChange={(e) => setClassStandard(e.target.value)}
                                                    min={1}
                                                />
                                            </div>
                                        </div>

                                        <div className="classes__form-row">
                                            <div className="classes__field">
                                                <label className="classes__label" htmlFor="classCapacity">Capacity</label>
                                                <input
                                                    className="classes__input"
                                                    type="number"
                                                    id="classCapacity"
                                                    placeholder="e.g. 120"
                                                    value={classCapacity}
                                                    onChange={(e) => setClassCapacity(e.target.value)}
                                                    min={1}
                                                />
                                            </div>

                                            <div className="classes__field">
                                                <label className="classes__label" htmlFor="classTeacher">Class Teacher</label>
                                                <select
                                                    className="classes__input"
                                                    id="classTeacher"
                                                    value={classTeacherId || ''}
                                                    onChange={(e) => {
                                                        setClassTeacherId(e.target.value || null);
                                                    }}
                                                >
                                                    <option value="">Select a teacher</option>
                                                    {staffList.map((s) => (
                                                        <option key={s.id} value={s.id}>
                                                            {getStaffName(s)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="classes__card">
                                        <h3 className="classes__card-title">Settings</h3>
                                        <div className="classes__toggle-row">
                                            <div className="classes__toggle-info">
                                                <span className="classes__toggle-label">Has Sections</span>
                                                <span className="classes__toggle-desc">Enable if this class is divided into sections</span>
                                            </div>
                                            <label className="classes__toggle">
                                                <input type="checkbox" checked={classHasSections} onChange={(e) => setClassHasSections(e.target.checked)} />
                                                <span className="classes__toggle-track" />
                                            </label>
                                        </div>
                                        <div className="classes__toggle-row">
                                            <div className="classes__toggle-info">
                                                <span className="classes__toggle-label">Active Status</span>
                                                <span className="classes__toggle-desc">Inactive classes are hidden from enrollment</span>
                                            </div>
                                            <label className="classes__toggle">
                                                <input type="checkbox" checked={classIsActive} onChange={(e) => setClassIsActive(e.target.checked)} />
                                                <span className="classes__toggle-track" />
                                            </label>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* ---- Section Form ---- */}
                            {isSectionDrawer && (
                                <>
                                    <div className="classes__card">
                                        <h3 className="classes__card-title">Section Details</h3>

                                        <div className="classes__form-row">
                                            <div className="classes__field">
                                                <label className="classes__label classes__label--required" htmlFor="sectionName">Section Name</label>
                                                <input
                                                    className={`classes__input ${sectionErrors.name ? 'classes__input--error' : ''}`}
                                                    type="text"
                                                    id="sectionName"
                                                    placeholder="e.g. A, B, C"
                                                    value={sectionName}
                                                    onChange={(e) => {
                                                        setSectionName(e.target.value);
                                                        if (sectionErrors.name) setSectionErrors((p) => ({ ...p, name: undefined }));
                                                    }}
                                                    maxLength={10}
                                                    autoFocus
                                                />
                                                {sectionErrors.name && <p className="classes__error">{sectionErrors.name}</p>}
                                            </div>

                                            <div className="classes__field">
                                                <label className="classes__label classes__label--required" htmlFor="sectionCapacity">Capacity</label>
                                                <input
                                                    className={`classes__input ${sectionErrors.capacity ? 'classes__input--error' : ''}`}
                                                    type="number"
                                                    id="sectionCapacity"
                                                    placeholder="e.g. 40"
                                                    value={sectionCapacity}
                                                    onChange={(e) => {
                                                        setSectionCapacity(e.target.value);
                                                        if (sectionErrors.capacity) setSectionErrors((p) => ({ ...p, capacity: undefined }));
                                                    }}
                                                    min={1}
                                                />
                                                {sectionErrors.capacity && <p className="classes__error">{sectionErrors.capacity}</p>}
                                            </div>
                                        </div>

                                        <div className="classes__field">
                                            <label className="classes__label" htmlFor="sectionTeacher">Class Teacher</label>
                                            <select
                                                className="classes__input"
                                                id="sectionTeacher"
                                                value={sectionTeacherId || ''}
                                                onChange={(e) => {
                                                    setSectionTeacherId(e.target.value || null);
                                                }}
                                            >
                                                <option value="">Select a teacher</option>
                                                {staffList.map((s) => (
                                                    <option key={s.id} value={s.user?.id ?? s.id}>
                                                        {getStaffName(s)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="classes__card">
                                        <h3 className="classes__card-title">Settings</h3>
                                        <div className="classes__toggle-row">
                                            <div className="classes__toggle-info">
                                                <span className="classes__toggle-label">Active Status</span>
                                                <span className="classes__toggle-desc">Inactive sections are hidden from normal operations</span>
                                            </div>
                                            <label className="classes__toggle">
                                                <input type="checkbox" checked={sectionIsActive} onChange={(e) => setSectionIsActive(e.target.checked)} />
                                                <span className="classes__toggle-track" />
                                            </label>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Timestamps */}
                            {(drawerMode === 'edit-class' || drawerMode === 'edit-section') && editCreatedAt && (
                                <div className="classes__timestamps">
                                    <span className="classes__timestamp">Created: {formatTimestamp(editCreatedAt)}</span>
                                    <span className="classes__timestamp">Updated: {formatTimestamp(editUpdatedAt)}</span>
                                </div>
                            )}
                        </form>

                        <div className="classes__drawer-footer">
                            <button className="classes__submit" type="submit" form="class-form" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <>
                                        <svg className="classes__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" strokeWidth="3" />
                                        </svg>
                                        {drawerMode?.startsWith('edit') ? 'Updating…' : 'Creating…'}
                                    </>
                                ) : drawerMode?.startsWith('edit') ? (
                                    `Update ${isClassDrawer ? 'Class' : 'Section'}`
                                ) : (
                                    `Create ${isClassDrawer ? 'Class' : 'Section'}`
                                )}
                            </button>
                            <button className="classes__cancel" type="button" onClick={closeDrawer} disabled={isSubmitting}>
                                Cancel
                            </button>
                        </div>
                    </aside>
                </>
            )}

            {/* ============= Delete Confirm ============= */}
            {deleteTarget && (
                <div className="classes__confirm-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="classes__confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="classes__confirm-title">Remove {deleteTarget.type === 'class' ? 'Class' : 'Section'}</h3>
                        <p className="classes__confirm-text">
                            Are you sure you want to remove <strong>{deleteTarget.name}</strong>?
                            {deleteTarget.type === 'class' && ' All associated sections will also be removed.'}
                            {' '}This action can be undone by an administrator.
                        </p>
                        <div className="classes__confirm-actions">
                            <button className="classes__confirm-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
                            <button className="classes__confirm-delete" onClick={confirmDelete}>Remove</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
